import React, { useState, useEffect, useCallback, useMemo } from 'react';

import { CenteredContainer, LoadingSkeleton } from '../../components';
import * as Styled from './styled';
import * as GStyled from '../../styles/components';
import { StyledText, Button } from '@leasequery/lq.react-component-library';
import { ReportResult } from '../../models';
import { fetchReportResultsCombineWithArchivedReports } from '../../api';
import RecentReportsTable from './recent-reports-table/RecentReportsTable';
import { useRouter } from 'next/router';
import ArchiveDialog from './archive-dialog/ArchiveDialog';
import {
  updateReportResultItem as updateReportResultsItem,
  updateReportResultStatusFromInprogressReports,
} from './helpers';
import { useAuth, useInprogressReports } from '../../context';
import withClient from '../../components/with-client/withClient';
import { ViewAllClients } from './client-toggle/ViewAllClients';

function ReportResults(): JSX.Element {
  const { user, client } = useAuth();
  const router = useRouter();
  const [reportResults, setReportResults] = useState<ReportResult[]>(null);
  const [displayCurrentClientOnly, setDisplayCurrentClientOnly] = useState(
    user.isMultiClientUser && user.canAccessMultipleClients,
  );
  const [archiveDialogIsOpen, setArchiveDialogIsOpen] = useState(false);
  const [archiveDialogData, setArchiveDialogData] =
    useState<ReportResult>(null);
  const [inprogressReport] = useInprogressReports();

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const resp = await fetchReportResultsCombineWithArchivedReports();
      setReportResults(resp);
    };
    fetchData();
  }, []);

  const viewArchiveClickHandler = useCallback(() => {
    router.push('/results/archives');
  }, [router]);

  const archiveReportClickHandler = useCallback((rowData: ReportResult) => {
    setArchiveDialogData(rowData);
    setArchiveDialogIsOpen(true);
  }, []);

  const archiveHandler = useCallback(
    (taskId: number, archiveName: string) => {
      const nextReportResults = updateReportResultsItem(reportResults, {
        taskId,
        isArchived: true,
        taskName: archiveName,
      });
      setReportResults(nextReportResults);
    },
    [reportResults],
  );

  const archivedReportNames = useMemo(
    () =>
      reportResults
        ? reportResults.reduce((acc, reportResult: ReportResult) => {
            if (reportResult.isArchived) {
              return [...acc, reportResult.taskName];
            }

            return acc;
          }, [])
        : [],
    [reportResults],
  );

  const displayedReportResults = useMemo(() => {
    const filteredReports =
      displayCurrentClientOnly && reportResults
        ? reportResults.filter((x) => x.companyName === client.companyName)
        : reportResults;
    return updateReportResultStatusFromInprogressReports(
      filteredReports,
      inprogressReport,
    );
  }, [
    displayCurrentClientOnly,
    reportResults,
    inprogressReport,
    client.companyName,
  ]);

  return (
    <CenteredContainer>
      <Styled.PageHeaderWrapper>
        <GStyled.PageHeader>
          Report Results for {user.firstName}
        </GStyled.PageHeader>
        <StyledText as="div">
          All report results will be stored for{' '}
          <StyledText fontWeight="bold">14 days</StyledText>. To archive the
          report results, select the &lsquo;archive&rsquo; icon to save the
          report permanently.
        </StyledText>
        <div>
          <Button onClick={viewArchiveClickHandler}>View Archive</Button>
        </div>
      </Styled.PageHeaderWrapper>
      <Styled.Section>
        <Styled.RecentReportTitleBar>
          <StyledText fontWeight="bold" size="xl">
            Recent Report Results
          </StyledText>
          {user.isMultiClientUser && user.canAccessMultipleClients && (
            <ViewAllClients
              filterStatus={displayCurrentClientOnly}
              updateFilterStatus={setDisplayCurrentClientOnly}
            />
          )}
        </Styled.RecentReportTitleBar>
        {!displayedReportResults ? (
          <LoadingSkeleton name="table" />
        ) : (
          <>
            <RecentReportsTable
              reportResults={displayedReportResults}
              onArchiveClick={archiveReportClickHandler}
            />
            {archiveDialogData && (
              <ArchiveDialog
                reportResultId={archiveDialogData.taskId}
                isOpen={archiveDialogIsOpen}
                onStateChange={setArchiveDialogIsOpen}
                onArchive={archiveHandler}
                archivedReportNames={archivedReportNames}
              />
            )}
          </>
        )}
      </Styled.Section>
    </CenteredContainer>
  );
}

export default withClient(ReportResults);
